/*
 *  -----------------------------------------------------------------------
 *    <copyright file="cm-review-report.ts" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *   This file has been created to replace request to the backend GET_CM_REVIEW_REPORT_DATA.
 *   Logic for this file is copied and rewritten from the backend.
 *   -----------------------------------------------------------------------
 *
 */
import { isPanelScatterPlot } from '@utils-clean/panel-helpers';
import { PanelFields } from 'queries/generated/fragments.generated';
import { PanelType, PanelModelType, RAGColourType } from 'types/graphql-global-types';
import PanelConstants from 'views/components/central-monitoring/panels/add-panel/panel-constants';

const getSiteSum = (field: string, sites: any) => {
    let sum = 0;
    (sites ?? []).forEach((site: any) => {
        sum += site.Fields[`Site.${field}`] ?? 0;
    });

    return sum;
};

export const calculateFieldsForStudySummary = (sites: any) => {
    const summary = {} as {
        screenedSubjects: number;
        enrolledSubjects: number;
        screenedFailedSubjects: number;
        randomisedSubjects: number;
        discontinuedSubjects: number;
        numCompleted: number;
        treatmentDiscontinuedSubjects: number;
    };
    summary.screenedSubjects = getSiteSum('subjectsScreened', sites);
    summary.enrolledSubjects = getSiteSum('subjectsEnrolled', sites);
    summary.screenedFailedSubjects = getSiteSum('subjectsScreenFailed', sites);
    summary.randomisedSubjects = getSiteSum('subjectsRandomized', sites);
    summary.discontinuedSubjects = getSiteSum('subjectsStudyDiscontinued', sites);
    summary.numCompleted = getSiteSum('subjectsStudyComplete', sites);
    summary.treatmentDiscontinuedSubjects = getSiteSum('subjectsTxDiscontinued', sites);

    return summary;
};

export const calculateRiskOverviewColumns = (panels: PanelFields[] = []) => {
    const riskOverviewColumns: {
        panelId: string;
        text: string;
        includeInSiteScoring: boolean;
        weight: number;
    }[] = [];
    const filterAndPushNonTrendPanels = (panel: PanelFields) => {
        if (panel !== null && panel.active && isPanelScatterPlot(panel))
            riskOverviewColumns.push({
                panelId: panel.id,
                text: panel.name,
                includeInSiteScoring: panel.includeInSiteScoring,
                weight: panel.weight,
            });
    };

    panels
        ?.filter((item1: PanelFields) => item1.includeInSiteScoring === true)
        ?.sort((a, b) => a.name.localeCompare(b.name))
        ?.forEach((panel: PanelFields) => {
            filterAndPushNonTrendPanels(panel);
        });

    panels
        ?.filter((item1: PanelFields) => item1.includeInSiteScoring === false)
        ?.sort((a, b) => a.name.localeCompare(b.name))
        ?.forEach((panel: PanelFields) => {
            filterAndPushNonTrendPanels(panel);
        });
    return riskOverviewColumns;
};

const colors = ['NONE', 'RED', 'AMBER', 'GREEN', 'PURPLE'];

// get data for Preview of Report and Risk Overview on Study Summary
// all scores are rounded to two decimal places
export const calculateRiskOverviewsAndStudySites = (
    sites: any,
    siteScores: any,
    onlyReviewed: boolean,
    sitesStatusesToExclude: string[],
    panels: PanelFields[],
    riskOverviewColumns: any,
    studySites?: any,
    isExcludeSites?: boolean,
) => {
    const totals = {} as any;
    const riskOverviews = [] as any;
    const newStudySites = [] as any;
    (siteScores ?? []).forEach((site: any) => {
        // find site in sites array from the selected dataset
        const calculatedSite = sites?.find((s: any) => s.Fields['Site.id'] === site.siteId);

        const siteName = calculatedSite?.Fields['Site.name'];
        const siteStatus = calculatedSite?.Fields['Site.status'];
        const siteReview = studySites ? studySites.find((item: any) => item.siteId === site.siteId) : null;

        if (onlyReviewed && (!siteReview || !siteReview.reviewed)) return;

        const overview = {
            kriScores: [] as any[],
            amberKris: 0,
            redKris: 0,
            amberDecisionKris: 0,
            redDecisionKris: 0,
            site: siteName,
            siteId: site.siteId,
            score: +site.score.toFixed(2),
            colour: colors[site.color],
            siteStatus,
            siteActiveDate: calculatedSite?.Fields['Site.activeDate'] ?? null,
            siteCountry: calculatedSite?.Fields['Site.country'] ?? null,
            siteMonitor: calculatedSite?.Fields['Site.monitor'] ?? null,
            numSubjects: calculatedSite?.Subjects.length ?? 0,
            numScreenedSubjects: calculatedSite?.Fields['Site.subjectsScreened'] ?? null,
            numEnrolledSubjects: calculatedSite?.Fields['Site.subjectsEnrolled'] ?? null,
            numDiscontinuedSubjects: calculatedSite?.Fields['Site.subjectsStudyDiscontinued'] ?? null,
            previousScore: site.previousScores?.length
                ? site.previousScores[site.previousScores.length - 1]?.score.toFixed(2)
                : -1,
            previousColour: site.previousScores?.length
                ? site.previousScores[site.previousScores.length - 1]?.color
                : 'NONE',
        };

        if (site.indicatorScores) {
            site.indicatorScores.forEach((kri: any) => {
                const panel = panels.find((panelItem: any) => kri.panelId === panelItem.id);
                if (panel == null || riskOverviewColumns.findIndex((c: any) => c.panelId === panel.id) < 0) return;

                const prevScore = kri.previousScores?.length
                    ? kri.previousScores[kri.previousScores.length - 1]?.score.toFixed(2)
                    : -1;
                const score = +kri.score.toFixed(2);

                overview.kriScores.push({
                    name: panel.name,
                    panelId: kri.panelId,
                    label:
                        panel.panelType === PanelType.MODEL &&
                        (panel.modelParams.type.toLowerCase() === PanelConstants.modelOptions.FUNNEL.toLowerCase() ||
                            panel.modelParams.type === PanelModelType.FUNNEL ||
                            panel.modelParams.type === PanelModelType.RATE_GROUP ||
                            panel.modelParams.type.toLowerCase() ===
                                PanelConstants.modelOptions.RATEGROUP.toLowerCase())
                            ? kri.direction === 'upper' || kri.direction === 0
                                ? 'U'
                                : kri.direction === 'lower' || kri.direction === 1
                                  ? 'L'
                                  : 'M'
                            : '',
                    score,
                    colour: colors[kri.color],
                    change: prevScore >= 0 ? (prevScore > score ? 'lower' : prevScore < score ? 'upper' : '') : '',
                    average: 0,
                });

                if (totals[panel.id]) totals[panel.id].push(kri.score);
                else totals[panel.id] = [kri.score];

                if (colors[kri.color] === RAGColourType.AMBER) {
                    overview.amberKris++;
                    if (panel.decision) overview.amberDecisionKris++;
                }
                if (colors[kri.color] === RAGColourType.RED) {
                    overview.redKris++;
                    if (panel.decision) overview.redDecisionKris++;
                }
            });
        }
        riskOverviews.push(overview);

        const previousScore = site.previousScores?.length
            ? site.previousScores[site.previousScores.length - 1].score?.toFixed(2)
            : null;
        const siteScore = +site.score.toFixed(2);

        const reviewStudySiteInfo = studySites ? studySites.find((item: any) => item.siteId === site.siteId) : null;

        const studySite = {
            site: siteName,
            siteId: site.siteId,
            siteActiveDate: calculatedSite?.Fields['Site.activeDate'] ?? null,
            screenedSubjects: calculatedSite?.Fields['Site.subjectsScreened'],
            screenedFailedSubjects: calculatedSite?.Fields['Site.subjectsScreenFailed'],
            enrolledSubjects: calculatedSite?.Fields['Site.subjectsEnrolled'],
            discontinuedSubjects: calculatedSite?.Fields['Site.subjectsStudyDiscontinued'],
            numRandomised: calculatedSite?.Fields['Site.subjectsRandomized'],
            numTreatmentDiscontinued: calculatedSite?.Fields['Site.subjectsTxDiscontinued'],
            numCompleted: calculatedSite?.Fields['Site.subjectsStudyComplete'],
            siteCountry: calculatedSite?.Fields['Site.country'] ?? null,
            siteMonitor: calculatedSite?.Fields['Site.monitor'] ?? null,
            numSubjects: calculatedSite?.Subjects.length ?? 0,
            // expect to get from centralMonitoringReview query
            reviewer: reviewStudySiteInfo?.reviewer ?? null,
            reviewed: reviewStudySiteInfo?.reviewed ?? false,
            reviewStartDate: reviewStudySiteInfo?.reviewStartDate ?? null,
            reviewCompleteDate: reviewStudySiteInfo?.reviewCompleteDate ?? null,
            reviewSummary: reviewStudySiteInfo?.reviewSummary ?? '',
            recordId: reviewStudySiteInfo?.recordId ?? null,
            updated: reviewStudySiteInfo?.updated ?? null,
            updatedBy: reviewStudySiteInfo?.updatedBy ?? null,
            // end
            siteScore,
            siteScoreChange: previousScore
                ? previousScore > siteScore
                    ? 'lower'
                    : previousScore < siteScore
                      ? 'upper'
                      : ''
                : '',
            siteScoreColour: colors[site.color],
            siteStatus,
            kriScores: [] as any[],
            observations: reviewStudySiteInfo?.observations,
        };
        site.indicatorScores.forEach((indicatorScore: any) => {
            const panel = panels.find((panelItem: any) => indicatorScore.panelId === panelItem.id);
            const prevIndicatorScore = indicatorScore.previousScores?.length
                ? +indicatorScore.previousScores[indicatorScore.previousScores.length - 1].score.toFixed(2)
                : 0;
            const currentScore = +indicatorScore.score.toFixed(2);
            studySite.kriScores.push({
                name: panel?.name ?? '',
                panelId: indicatorScore.panelId,
                score: currentScore,
                change: prevIndicatorScore
                    ? prevIndicatorScore > currentScore
                        ? 'lower'
                        : prevIndicatorScore < currentScore
                          ? 'upper'
                          : ''
                    : '',
                colour: colors[indicatorScore.color],
                average: 0,
                label:
                    panel.panelType === PanelType.MODEL &&
                    (panel.modelParams.type.toLowerCase() === PanelConstants.modelOptions.FUNNEL.toLowerCase() ||
                        panel.modelParams.type === PanelModelType.FUNNEL ||
                        panel.modelParams.type === PanelModelType.RATE_GROUP ||
                        panel.modelParams.type.toLowerCase() === PanelConstants.modelOptions.RATEGROUP.toLowerCase())
                        ? indicatorScore.direction === 'upper' || indicatorScore.direction === 0
                            ? 'U'
                            : indicatorScore.direction === 'lower' || indicatorScore.direction === 1
                              ? 'L'
                              : 'M'
                        : '',
            });
        });

        newStudySites.push(studySite);
    });

    for (const key in totals) {
        const average =
            totals[key].length > 1
                ? (totals[key].reduce((a: any, b: any) => a + b) / totals[key].length).toFixed(2)
                : 0;

        riskOverviews.forEach((overview: any) => {
            const score = overview.kriScores.find((score: any) => score.panelId == key);
            if (score) score.average = average;
        });
        newStudySites.forEach((item: any) => {
            const score = item.kriScores.find((el: any) => el.panelId === key);

            if (score) score.average = average;
        });
    }
    return {
        riskOverviews,
        studySites: newStudySites,
    };
};
